import { Input, Selector } from '@casumo/dojo-react';
import { Close, Search } from '@casumo/dojo-react-icons';
import { memo, useState } from 'react';
import useLoadGoogleMapsScript from '../../api/hooks/useLoadGoogleMapsScript.tsx';
import usePlacesAutocomplete, { TAutocompletePrediction } from '../../api/hooks/usePlacesAutocomplete.tsx';
import Drawer from '../../design-system/drawer.tsx';
import Loader from '../../design-system/loader.tsx';
import Skeleton from '../../design-system/skeleton.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { FieldType, TAutoCompleteAddressInput } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import { GOOGLE_API_KEY } from '../../utils/envConstants.ts';
import { mapDetailedAddress } from '../../utils/utils.ts';
import { EnterAddressManuallyText } from '../enterAddressManuallyText.tsx';

const AutoCompleteAddressInput = ({ label, name, dialogTitle }: TAutoCompleteAddressInput) => {
  const { formData, setFormData, metadata, setMetadata, setErrors, setCondition, 
    setIsAddressSearchOpen, isAddressSearchOpen, setValidateOnRender } = useRegistrationStore();

  const [searchValue, setSearchValue] = useState<string>(formData?.[name] || '');
  const {
    predictions,
    fetchDetailedAddress,
    isLoading,
  } = usePlacesAutocomplete(searchValue, formData?.countryOfResidence, metadata?.selectedProvinceCode);

  const { isScriptLoaded, error } = useLoadGoogleMapsScript('places', GOOGLE_API_KEY);

  const handleIsOpen = (isOpen: boolean) => {
    setIsAddressSearchOpen(isOpen);
  };

  const handleCheck = async (prediction: TAutocompletePrediction) => {
    setErrors({
      houseNumber: '',
      street:      '',
      city:        '',
      postcode:    '',
      province:    '',
    });
    setFormData({
      houseNumber: '',
      street:      '',
      city:        '',
      postcode:    '',
      province:    '',
    });

    setIsAddressSearchOpen(false);
    setFormData({ [name]: prediction.description });
    setSearchValue(prediction.description);

    const detailedAddress = await fetchDetailedAddress(prediction);
    if (!detailedAddress) {
      return;
    }

    const mappedAddress = mapDetailedAddress(detailedAddress);

    const { houseNumber, street, city, postcode, provinceCode } = mappedAddress;

    setFormData({
      houseNumber,
      street,
      city,
      postcode,
      province: provinceCode,
    });

    setValidateOnRender(true);

    setMetadata({ provinceCode });

    setCondition('enterAddressManually', true);
  };

  const readOnlyInput = () => (
    <Input
      id="countrySelectorInput"
      name={name}
      label={label}
      value={formData?.[name]}
      onKeyDown={e => e.key === 'Enter' && handleIsOpen(true)}
      sufix={<span className="text-[24px]"><Search /></span>}
      onClick={() => handleIsOpen(true)}
      readOnly
      data-testid={TEST_ID.COMBOBOX_TRIGGER + 'addressSearch'}
    />
  );

  if (error) {
    return <div>Error loading Google Maps script: {error}</div>;
  }

  if (!isScriptLoaded) {
    return <Skeleton height="h-[52px]" />;
  }

  return (
    <>
      {readOnlyInput()}
      <Drawer
        isOpen={isAddressSearchOpen}
        setIsOpen={handleIsOpen}>
        <div className="flex flex-col h-full w-full p-md overflow-hidden">
          <div className="flex justify-between text-white pb-sm font-bold">
            <h1 className="text-h-lg">{dialogTitle}</h1>
            <span className="p-2xs cursor-pointer text-[24px]" onClick={() => handleIsOpen(false)}><Close /></span>
          </div>
          <Input
            id="addressSearch"
            name="search"
            placeholder={label}
            variant="dark"
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            value={searchValue}
            prefix={<span className="text-[24px]"><Search /></span>}
            sufix={isLoading ? <Loader /> : null}
            clearable
            onClear={() => setSearchValue('')}
            autoFocus
            data-testid={TEST_ID.SEARCH + 'address'}
          />
          <Selector name="country" direction="vertical" variant="dark"
            className="flex-1 w-full mt-md px-0.5 overflow-y-auto">
            {predictions.length
              ? predictions.map((prediction) => (
                <Selector.Radio
                  onClick={() => handleCheck(prediction)}
                  className="flex gap-3xs"
                  key={prediction.place_id}
                  data-testid={TEST_ID.SEARCH_RESULT + 'address'}
                >
                  {prediction.description}
                </Selector.Radio>
              ))
              : searchValue && !isLoading
                ? <div className="flex justify-center">
                    <EnterAddressManuallyText
                      name="enterManually"
                      fieldType={FieldType.EnterAddressManuallyText}
                      styles={{ label: 'text-white', link: 'text-white underline' }}
                      //TODO: Add CMS fields when implementing a new country with different language
                      label="No result found."
                      link="Enter it manually"
                      toggleEnterAddressManually={true} />
                  </div>
                : null
            }
          </Selector>
        </div>
      </Drawer>
    </>
  );
};

const Memoized = memo(AutoCompleteAddressInput);
Memoized.displayName = 'AutoCompleteAddressInput';

export default Memoized;
